"use client";

import React, { useEffect, useRef, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Activity, Clock, Users, ShieldCheck } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedNumber({
  value,
  isVisible
}: {
  value: number;
  isVisible: boolean;
}) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    const duration = 2000;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCurrent(Math.floor(easeProgress * value));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCurrent(value);
      }
    };
    window.requestAnimationFrame(step);
  }, [value, isVisible]);
  return <>{current}</>;
}
const statsData = [{
  icon: Clock,
  value: 40,
  suffix: "%",
  title: "Mer effektiv",
  description: "Vår AI-drevne oppfølging sørger for at potensielle kjøpere får svar umiddelbart, døgnet rundt.",
  progressValue: 40
}, {
  icon: Users,
  value: 65,
  suffix: "%",
  title: "Flere Leads",
  description: "Automatiserte kampanjer og smart segmentering øker tilstrømmingen av kvalifiserte interessenter.",
  progressValue: 65
}, {
  icon: Activity,
  value: 15,
  suffix: "t+",
  title: "Spart per uke",
  description: "Frigjør tid fra administrative oppgaver slik at du kan fokusere på visninger og kundekontakt.",
  progressValue: 85
}, {
  icon: ShieldCheck,
  value: 100,
  suffix: "%",
  title: "Datakontroll",
  description: "Sikker håndtering av kundedata i tråd med norske personvernregler og bransjestandarder.",
  progressValue: 100
}];
export default function ClinicalImpactByTheNumbers() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223267" id="resultater-som-teller" className="bg-background text-foreground py-24 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[120px] pointer-events-none opacity-40 animate-pulse" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[100px] pointer-events-none opacity-20" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        
        {}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-default font-bold mb-6 tracking-tighter">
            Resultater som <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">teller</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Xora leverer målbar effektivitet for eiendomsmeglere ved å kombinere avansert AI med dyp bransjekunnskap.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {statsData.map((stat, i) => <div key={i} data-index={i} className={`relative group rounded-3xl border border-border/10 bg-card/30 backdrop-blur-2xl p-8 shadow-2xl transition-all duration-700 ease-out hover:bg-card/50 hover:border-primary/40 hover:shadow-primary/10 hover:-translate-y-1 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              {}
              <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/30 group-hover:scale-110 transition-transform duration-300">
                <stat.icon className="h-6 w-6" />
              </div>

              {}
              <div className="mb-2 flex items-baseline gap-1">
                <span className="text-5xl md:text-7xl font-default font-bold text-white tracking-tighter">
                  <AnimatedNumber value={stat.value} isVisible={sectionVisible} />
                </span>
                <span className="text-3xl md:text-4xl font-default font-bold text-primary">
                  {stat.suffix}
                </span>
              </div>

              {}
              <h3 className="text-xl font-default font-bold text-card-foreground mb-3">
                {stat.title}
              </h3>
              <p className="text-sm text-muted-foreground font-default mb-8 leading-relaxed">
                {stat.description}
              </p>

              {}
              <div className="absolute bottom-8 left-8 right-8">
                <Progress value={sectionVisible ? stat.progressValue : 0} className="h-1 bg-muted/20" />
              </div>
            </div>)}
        </div>

      </div>
    </section>;
}